import React, { useState } from 'react';
import './Pagination.css';

const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange, 
  totalItems, 
  itemsPerPage, 
  onItemsPerPageChange,
  loading,
  isAdmin,
  levelFilter,
  tempLevelFilter,
  onLevelFilterChange,
  onApplyLevelFilter,
  onClearLevelFilter,
  levelFilterError
}) => {
  const [goToPage, setGoToPage] = useState('');

  const handleLevelFilterKeyPress = (e) => {
    if (e.key === 'Enter' && (tempLevelFilter?.min || tempLevelFilter?.max)) {
      onApplyLevelFilter();
    }
  };

  const getVisiblePages = () => {
    if (totalPages === 1) {
      return [1];
    }

    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (let i = Math.max(2, currentPage - delta); 
         i <= Math.min(totalPages - 1, currentPage + delta); 
         i++) {
      range.push(i);
    }

    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  const handleGoToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(goToPage);
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      onPageChange(pageNumber);
      setGoToPage('');
    }
  };

  const handleGoToPageInput = (e) => {
    const value = e.target.value;
    // Only allow numbers
    if (value === '' || /^\d+$/.test(value)) {
      setGoToPage(value);
    }
  };

  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div className="pagination-container">
      {isAdmin ? (
        <div className="level-filter">
          <label>Level Filter:</label>
          <input
            type="number"
            placeholder="Min"
            value={tempLevelFilter?.min || ''}
            onChange={(e) => onLevelFilterChange && onLevelFilterChange('min', e.target.value)}
            onKeyDown={handleLevelFilterKeyPress}
            min="0"
            max="100000"
            className="level-filter-input"
          />
          <span>-</span>
          <input
            type="number"
            placeholder="Max"
            value={tempLevelFilter?.max || ''}
            onChange={(e) => onLevelFilterChange && onLevelFilterChange('max', e.target.value)}
            onKeyDown={handleLevelFilterKeyPress}
            min="0"
            max="100000"
            className="level-filter-input"
          />
          <button
            className="apply-level-filter-btn"
            onClick={onApplyLevelFilter}
            disabled={!tempLevelFilter?.min && !tempLevelFilter?.max}
          >
            Apply
          </button>
          {(levelFilter?.min || levelFilter?.max) && (
            <button
              className="clear-level-filter-btn"
              onClick={onClearLevelFilter}
              title="Clear level filter"
            >
              Clear
            </button>
          )}
          {levelFilterError && (
            <span className="level-filter-error">{levelFilterError}</span>
          )}
        </div>
      ): (<div></div>)}
      <div className="pagination-info">
        <div className="pagination-controls">
          <div className="pagination">
            <button
              className="page-btn prev-btn"
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {totalPages && getVisiblePages().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="page-dots">...</span>
                ) : (
                  <button
                    className={`page-btn ${currentPage === page ? 'active' : ''}`}
                    onClick={() => currentPage !== page && onPageChange(page)}
                  >
                    {page}
                  </button>
                )}
              </React.Fragment>
            ))}

            <button
              className="page-btn next-btn"
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>

          <form className="go-to-page" onSubmit={handleGoToPage}>
            <label htmlFor="page-input">Go to:</label>
            <input
              id="page-input"
              type="text"
              value={goToPage}
              onChange={handleGoToPageInput}
              placeholder="Page"
              className="page-input"
              min="1"
              max={totalPages}
            />
            <button 
              type="submit" 
              className="go-btn"
              disabled={!goToPage || parseInt(goToPage) < 1 || parseInt(goToPage) > totalPages}
            >
              Go
            </button>
          </form>
        </div>

        <div className="items-count">
          <div className="items-per-page">
            <label>Show: </label>
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange && onItemsPerPageChange(parseInt(e.target.value))}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            <span> per page</span>
          </div>

          <span className="showing-info">
            {loading ? 'Loading...' : totalItems === 1 ? 'Showing 1 cofounder' : `Showing ${startItem}-${endItem} of ${totalItems} cofounders`}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Pagination;